import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Campaign, CampaignChannel, CampaignStatus, PaginatedResponse } from '../../types';
import { fetchCampaigns, deleteCampaign, launchCampaign, cancelCampaign } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';

const STATUS_STYLE: Record<CampaignStatus, { bg: string; color: string }> = {
  draft:     { bg: '#f1f5f9', color: '#475569' },
  scheduled: { bg: '#dbeafe', color: '#1d4ed8' },
  running:   { bg: '#dcfce7', color: '#166534' },
  paused:    { bg: '#fef9c3', color: '#854d0e' },
  completed: { bg: '#ede9fe', color: '#6d28d9' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

const CHANNEL_ICON: Record<CampaignChannel, string> = {
  whatsapp: '💬',
  sms:      '📱',
  email:    '✉️',
};

const fmtNum = (n: number) => n.toLocaleString('en-IN');

const CampaignCard: React.FC<{
  campaign: Campaign;
  onLaunch: (id: number) => void;
  onCancel: (id: number) => void;
  onDelete: (id: number) => void;
  onClick: () => void;
}> = ({ campaign: c, onLaunch, onCancel, onDelete, onClick }) => {
  const st  = STATUS_STYLE[c.status];
  const pct = c.total_targeted > 0 ? Math.round((c.total_sent / c.total_targeted) * 100) : 0;

  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 12, border: '1px solid var(--border)',
      padding: '18px 20px', cursor: 'pointer', transition: 'box-shadow .15s',
    }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>{CHANNEL_ICON[c.channel]}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {c.segment?.name ?? 'No segment'} &middot; {c.type}
            </div>
          </div>
        </div>
        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, ...st }}>
          {c.status}
        </span>
      </div>

      {/* Progress bar — visible once launched */}
      {c.total_targeted > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>{fmtNum(c.total_sent)} sent of {fmtNum(c.total_targeted)}</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2 }}>
            <div style={{ height: 4, width: `${pct}%`, background: '#7c3aed', borderRadius: 2, transition: 'width .3s' }} />
          </div>
        </div>
      )}

      {/* Counters */}
      {c.total_targeted > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { label: 'Delivered', val: c.total_delivered, color: '#16a34a' },
            { label: 'Read',      val: c.total_read,      color: '#0369a1' },
            { label: 'Replied',   val: c.total_replied,   color: '#7c3aed' },
            { label: 'Failed',    val: c.total_failed,    color: '#dc2626' },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color }}>{fmtNum(val)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
        {c.status === 'draft' && (
          <>
            <button className="btn btn-primary" style={{ padding: '4px 14px', fontSize: 12 }} onClick={() => onLaunch(c.id)}>
              ▶ Launch
            </button>
            <button className="row-btn row-btn-danger" onClick={() => onDelete(c.id)}>Delete</button>
          </>
        )}
        {c.status === 'running' && (
          <button className="btn btn-secondary" style={{ padding: '4px 14px', fontSize: 12 }} onClick={() => onCancel(c.id)}>
            ⏸ Cancel
          </button>
        )}
        {c.status === 'scheduled' && (
          <button className="btn btn-primary" style={{ padding: '4px 14px', fontSize: 12 }} onClick={() => onLaunch(c.id)}>
            ▶ Launch Now
          </button>
        )}
      </div>
    </div>
  );
};

const CampaignsPage: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const [data, setData]       = useState<PaginatedResponse<Campaign> | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState('');
  const [channel, setChannel] = useState('');
  const [page, setPage]       = useState(1);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetchCampaigns({ page, status: status || undefined, channel: channel || undefined, tenant_id: tenantId || undefined });
      setData(res);
    } catch { setError('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, [page, status, channel, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, channel]);

  const handleLaunch = async (id: number) => {
    if (!window.confirm('Launch this campaign?')) return;
    try { await launchCampaign(id); load(); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Launch failed'); }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('Cancel this campaign? Messages already in queue will be skipped.')) return;
    try { await cancelCampaign(id); load(); }
    catch { alert('Cancel failed'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await deleteCampaign(id); load(); }
    catch { alert('Delete failed'); }
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Campaigns</h1>
          <p className="page-meta"><strong>{data?.total ?? 0}</strong> total campaigns</p>
        </div>
        <div className="page-hd-right">
          <button className="btn btn-ghost" onClick={() => history.push('/campaigns/segments')}>
            Segments
          </button>
          <button className="btn btn-primary" onClick={() => history.push('/campaigns/new')}>
            + New Campaign
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="filter-select" value={channel} onChange={e => setChannel(e.target.value)}>
          <option value="">All Channels</option>
          <option value="whatsapp">💬 WhatsApp</option>
          <option value="sms">📱 SMS</option>
          <option value="email">✉️ Email</option>
        </select>
        {(status || channel) && (
          <button className="btn btn-ghost" onClick={() => { setStatus(''); setChannel(''); }}>Clear</button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? <div className="spinner">Loading…</div> : (
        <>
          {data && data.data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📣</div>
              <div style={{ fontWeight: 600 }}>No campaigns yet</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Click <strong>+ New Campaign</strong> to create your first broadcast</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
              {data?.data.map(c => (
                <CampaignCard
                  key={c.id}
                  campaign={c}
                  onLaunch={handleLaunch}
                  onCancel={handleCancel}
                  onDelete={handleDelete}
                  onClick={() => history.push(`/campaigns/${c.id}`)}
                />
              ))}
            </div>
          )}

          {data && data.last_page > 1 && (
            <div className="pager" style={{ marginTop: 20 }}>
              <span>Page {data.current_page} of {data.last_page}</span>
              <div className="pager-btns">
                <button className="pager-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <button className="pager-btn" disabled={page === data.last_page} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignsPage;
